// Global Instructions Rule Applied!
//
// Generic agent states strip — the "what is true right now" row shared by the
// company Dashboard and the two deal strips (origination Dashboard, Execution
// tab). Persistent states always show; transitory/ask states only while
// active. The old pill + click-popover treatment is replaced by a responsive
// row of StateCards (owner, 2026-08-25) with the popover's content and
// maintenance actions inline, plus Create task.
//
// COLLAPSIBLE (Option A, owner 2026-08-25): a chevron in the section header
// folds the card row down to the original one-line pill strip — label,
// condition and evidence date stay readable, hovering a pill shows the
// narrative (the old pill behaviour), clicking a pill expands back to the
// cards. The position persists per surface through the per-user app
// preference blob (agent_states_collapsed — the PREF_APP_CURRENT_AREA
// precedent), and the chevron itself now comes from the shared
// useSectionCollapse hook so the drafts and signals sections beneath this one
// fold the same way (owner, 2026-09-06).
//
// Data access is injected (fetch/override/createTask), so this component owns
// only presentation, the modals, and the refresh handshake — the thin
// CompanyStatesStrip / DealStatesStrip wrappers bind their services and map
// their row shapes onto StateItem.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Input, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useSectionCollapse } from '../../../core/hooks/useSectionCollapse.tsx'
import { TaskForm, TaskFormRef } from '../../tasks/forms/TaskForm.tsx'
import { StateCard, StateItem } from './StateCard.tsx'
import { conditionColor, kindColor } from './stateColors.ts'

export interface StatesStripProps {
  title?: string
  fetchStates: () => Promise<StateItem[]>
  overrideState: (args: {
    stateKey: string
    action: 'set_condition' | 'edit_summary' | 'close'
    stateId?: string | null
    conditionKey?: string | null
    summary?: string | null
  }) => Promise<void>
  /** Absent = no Create task button on the cards (states-only surfaces). */
  createTaskFromState?: (stateId: string, taskRecord: unknown) => Promise<unknown>
  /** Pre-selects assignees in the create-task form. Never auto-assigns. */
  defaultAssignees?: Array<{ label: string; value: string }>
  /** Fired after a task is created so the host can refresh its Tasks panel. */
  onTaskCreated?: () => void
  /** 'company' | 'deal' — modal copy only. */
  subjectNoun?: string
  /** Surface key for the collapsed/expanded preference (e.g.
   *  'company_dashboard'). When set, the header gains a chevron and the strip
   *  can fold to the one-line pill row; the strip STARTS collapsed (owner
   *  2026-08-25) and the position persists per surface across sessions.
   *  Absent = always expanded (no chevron). */
  collapseKey?: string
  /** The starting position for a surface the user has not folded or
   *  unfolded yet. Company and deal strips start collapsed (owner
   *  2026-08-25); the prospect strip starts open (owner 2026-09-03). A
   *  persisted user choice always wins. */
  defaultCollapsed?: boolean
  /** Hands `load` to the host form so its Refresh button reloads this panel.
   *  Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Rendered immediately after the title, qualifying the heading. The LP
   *  strip passes the derived LP status Tag through it (owner decision 7,
   *  Phase 7: the status is NOT a state — no evidence, no confidence, nothing
   *  to override — so it rides in the chrome, not in the card row). It sat
   *  beside 'Agent-maintained' at the far right until 2026-09-06; a bare
   *  'Target' out there said nothing about what it was a status of. */
  titleSuffix?: React.ReactNode
  /** Replaces the 'No states yet.' line when the strip has nothing to show.
   *  The LP strip uses it to say the relationship manager is not following
   *  this account yet, which is the true reason for an empty LP strip until
   *  the manager is provisioned. */
  emptyText?: React.ReactNode
}

export const StatesStrip: React.FC<StatesStripProps> = ({
  title = 'Current State',
  fetchStates,
  overrideState,
  createTaskFromState,
  defaultAssignees = [],
  onTaskCreated,
  subjectNoun = 'company',
  collapseKey,
  defaultCollapsed = true,
  onRefreshReady = null,
  titleSuffix = null,
  emptyText = 'No states yet.'
}) => {
  const [states, setStates] = useState<StateItem[]>([])
  // Collapsing here swaps the cards for pills rather than hiding the body, so
  // the hook supplies the chevron and the stored position and this component
  // keeps its own two renders.
  const { collapsed, setCollapsed, chevron } = useSectionCollapse(collapseKey, defaultCollapsed, {
    expand: 'Expand to state cards',
    collapse: 'Collapse to state pills'
  })
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<StateItem | null>(null)
  const [editSummary, setEditSummary] = useState('')
  const [closing, setClosing] = useState<StateItem | null>(null)
  const [taskFor, setTaskFor] = useState<StateItem | null>(null)
  const [creatingTask, setCreatingTask] = useState(false)
  const taskFormRef = useRef<TaskFormRef>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setStates(await fetchStates())
    } catch (error) {
      console.error('[internal-agents] states load failed:', error)
      setStates([])
    } finally {
      setLoading(false)
    }
  }, [fetchStates])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale — onRefreshReady joins the host form's
  // refresh handshake (the DocumentsTab contract).
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  const visible = useMemo(
    () => states.filter((s) => s.kind === 'persistent' || s.status === 'active'),
    [states]
  )

  const handleSetCondition = async (state: StateItem, conditionKey: string) => {
    try {
      await overrideState({
        stateKey: state.stateKey,
        action: 'set_condition',
        conditionKey
      })
      toast.success('Condition updated — your override wins until newer evidence')
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to set condition')
    }
  }

  const handleEditSummaryConfirm = async () => {
    if (!editing) return
    const state = editing
    setEditing(null)
    try {
      await overrideState({
        stateKey: state.stateKey,
        action: 'edit_summary',
        stateId: state.id,
        summary: editSummary.trim()
      })
      toast.success('Summary updated')
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to update summary')
    }
  }

  const handleCloseConfirm = async () => {
    if (!closing) return
    const state = closing
    setClosing(null)
    try {
      await overrideState({
        stateKey: state.stateKey,
        action: 'close',
        stateId: state.id
      })
      toast.success('State dismissed')
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to dismiss state')
    }
  }

  const handleCreateTaskConfirm = async () => {
    if (!taskFor?.id || !taskFormRef.current || !createTaskFromState) return
    let taskRecord: unknown
    try {
      // TaskForm owns validation and mints the task id, exactly as the
      // signal-card flow does.
      taskRecord = await taskFormRef.current.validateFields()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Please check the task details')
      return
    }
    setCreatingTask(true)
    try {
      await createTaskFromState(taskFor.id, taskRecord)
      setTaskFor(null)
      toast.success('Task created')
      await load()
      // The Tasks section is a sibling panel with its own fetch.
      onTaskCreated?.()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to create task')
    } finally {
      setCreatingTask(false)
    }
  }

  // Collapsed form: the original one-line pill strip. Hover = the narrative
  // (the old pill behaviour); click = expand back to the cards.
  const pillRow = (
    <div className='flex flex-wrap items-center gap-2'>
      {visible.map((state) => {
        const pillDate = state.lastEvidenceAt ?? state.lastConfirmedAt
        return (
          <Tooltip
            key={state.id ?? state.stateKey}
            title={state.summary ?? 'No narrative yet.'}
            mouseEnterDelay={0.4}
          >
            <span
              className='cursor-pointer inline-flex'
              role='button'
              tabIndex={0}
              onClick={() => setCollapsed(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setCollapsed(false)
              }}
            >
              <Tag
                color={
                  state.kind === 'persistent'
                    ? conditionColor(state.conditionRank)
                    : kindColor(state.kind)
                }
                className='m-0 px-2 py-0.5 text-[12px] select-none'
              >
                {state.kind === 'persistent'
                  ? `${state.label}: ${state.conditionLabel ?? 'Unknown'}`
                  : state.label}
                {pillDate ? (
                  <span className='opacity-60 ml-1'>· {dayjs(pillDate).format('MM/DD/YY')}</span>
                ) : null}
                {state.userSet ? <span className='opacity-60 ml-1'>· user</span> : null}
              </Tag>
            </span>
          </Tooltip>
        )
      })}
    </div>
  )

  // Pre-fill from the state: label + condition is the one-line what, the
  // summary the context. The user edits both before saving.
  const taskInitialValues = useMemo(
    () =>
      taskFor
        ? {
            subject: (taskFor.kind === 'persistent'
              ? `${taskFor.label}: ${taskFor.conditionLabel ?? 'Unknown'}`
              : taskFor.label
            ).slice(0, 80),
            notes: taskFor.summary || '',
            assignedTo: defaultAssignees,
            dueDate: null,
            completed: false
          }
        : {},
    [taskFor, defaultAssignees]
  )

  return (
    <FormSection
      data-component='StatesStrip'
      title={title}
      titleColor='blue'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerLeading={chevron}
      titleSuffix={titleSuffix}
      headerActions={<Tag color='blue' className='m-0'>Agent-maintained</Tag>}
    >
      {visible.length === 0 && !loading ? (
        <div className='text-gray-400 text-xs py-1'>{emptyText}</div>
      ) : collapsed ? (
        pillRow
      ) : (
        <div
          className='grid gap-2'
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}
        >
          {visible.map((state) => (
            <StateCard
              key={state.id ?? state.stateKey}
              state={state}
              onSetCondition={handleSetCondition}
              onEditSummary={(target) => {
                setEditSummary(target.summary ?? '')
                setEditing(target)
              }}
              onDismiss={(target) => setClosing(target)}
              onCreateTask={createTaskFromState ? (target) => setTaskFor(target) : undefined}
            />
          ))}
        </div>
      )}

      <Modal
        title={`Edit ${editing?.label ?? ''} summary`}
        open={editing != null}
        onOk={handleEditSummaryConfirm}
        okText='Save'
        okButtonProps={{ disabled: !editSummary.trim() }}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        <p className='text-gray-600'>
          Your edit is authoritative — the agent keeps it until newer evidence post-dates it.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={500}
          value={editSummary}
          onChange={(e) => setEditSummary(e.target.value)}
          placeholder='One-line current narrative'
        />
      </Modal>

      <Modal
        title={`Dismiss ${closing?.label ?? ''}`}
        open={closing != null}
        onOk={handleCloseConfirm}
        okText='Dismiss'
        okButtonProps={{ danger: true }}
        onCancel={() => setClosing(null)}
        destroyOnClose
      >
        <p className='text-gray-600'>
          Dismissing marks this state as not (or no longer) real. The agent will not re-open it
          unless newer evidence appears.
        </p>
      </Modal>

      <Modal
        title='Create task from state'
        open={taskFor != null}
        onOk={handleCreateTaskConfirm}
        okText='Create task'
        confirmLoading={creatingTask}
        onCancel={() => setTaskFor(null)}
        destroyOnClose
        width={560}
      >
        <p className='text-gray-600'>
          Pre-filled from the state — edit anything before saving. The task is linked to this{' '}
          {subjectNoun} and appears in its Tasks list.
        </p>
        {/* TaskForm is built for the full-height detail panel: its root is
            `h-full flex flex-col` with the notes editor as flex-1, so left
            unbounded it grows past 2000px. Bounding the wrapper lets the
            editor size itself to what is left — compact, same controls. */}
        <div style={{ height: 430 }}>
          <TaskForm ref={taskFormRef} initialValues={taskInitialValues} />
        </div>
      </Modal>
    </FormSection>
  )
}
